import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getComicById, getHotComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  const iconSize = size === 'md' ? 'text-sm' : 'text-xs';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className="w-4 h-4 flex items-center justify-center">
          <i
            className={`${iconSize} ${
              star <= Math.floor(rating)
                ? 'ri-star-fill text-amber-400'
                : star - 0.5 <= rating
                ? 'ri-star-half-line text-amber-400'
                : 'ri-star-line text-foreground-300'
            }`}
          ></i>
        </span>
      ))}
    </div>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = useMemo(() => getComicById(Number(id)), [id]);
  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return getHotComics(6).filter((c) => c.id !== comic.id).slice(0, 4);
  }, [comic]);

  const currentCategory = useMemo(
    () => (comic ? categories.find((c) => c.slug === comic.category) : null),
    [comic]
  );

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <span className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-2xl">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </span>
            <h1 className="text-xl font-bold font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已被移除</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画免费在线观看`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters_list[0]?.updateDate || '2024-01-01',
    dateModified: comic.updateDate,
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Thing',
      name: '韩国漫画',
    },
    publisher: {
      '@type': 'Organization',
      name: '韩漫大全',
      url: 'https://www.hanman123.com/',
    },
  };

  return (
    <>
      <PageMeta
        title={`${comic.title} - 韩国漫画免费在线观看 - 韩漫大全`}
        description={`${comic.title} - ${comic.description.substring(0, 120)}。韩漫大全提供${comic.title}无删减漫画免费在线观看，作者：${comic.author}，共${comic.chapters}话，状态：${comic.status}。`}
        keywords={`${comic.title},韩国漫画,韩漫大全,${comic.author},${currentCategory?.name || ''},无删减漫画免费在线观看`}
        canonicalUrl={`https://www.hanman123.com/article/${comic.id}`}
        ogType="article"
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />

        <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类', href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          {/* Article Header */}
          <article className="py-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画封面`}
                    title={`${comic.title} - 韩国漫画免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex items-center gap-4 mb-4">
                  <StarRating rating={comic.rating} size="md" />
                  <span className="text-sm text-foreground-500">{comic.rating} 分</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {(comic.views / 10000).toFixed(0)}万次阅读
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
                  <div>
                    <span className="text-foreground-400">作者：</span>
                    <span className="text-foreground-700">{comic.author}</span>
                  </div>
                  <div>
                    <span className="text-foreground-400">分类：</span>
                    <Link
                      to={`/category/${comic.category}`}
                      className="text-primary-600 hover:text-primary-700 cursor-pointer"
                    >
                      {currentCategory?.name || comic.category}
                    </Link>
                  </div>
                  <div>
                    <span className="text-foreground-400">章节数：</span>
                    <span className="text-foreground-700">{comic.chapters}话</span>
                  </div>
                  <div>
                    <span className="text-foreground-400">最近更新：</span>
                    <span className="text-foreground-700">{comic.updateDate}</span>
                  </div>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line mr-1.5"></i>
                    开始阅读第一话
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-1.5"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="pb-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold font-heading text-foreground-900">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-sort-asc mr-1"></i>
                  正序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {comic.chapters_list.map((chapter) => (
                <a
                  key={chapter.id}
                  href="#"
                  className="flex items-center justify-between px-4 py-3 bg-background-100 rounded-lg hover:bg-primary-100/50 transition-colors cursor-pointer group"
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updateDate}</span>
                </a>
              ))}
            </div>
            {comic.chapters > 8 && (
              <div className="text-center mt-5">
                <button className="px-6 py-2.5 bg-background-100 text-foreground-600 text-sm font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  展开全部 {comic.chapters} 话
                  <i className="ri-arrow-down-s-line ml-1"></i>
                </button>
              </div>
            )}
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="pb-16">
              <h2 className="text-lg font-bold font-heading text-foreground-900 mb-5">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-product-shop>
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/article/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-100">
                      <img
                        src={rc.coverImage}
                        alt={`${rc.title} - 韩国漫画`}
                        title={`${rc.title} - 韩国漫画推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}